import React, { useState } from "react";
import { useTranslation } from "utilities/i18n";
import { nameIsUnassigned } from "utilities/stringUtils";
import { NavLink, Redirect, Route, Switch, useParams } from "polyrouter";
import {
  useSiteIdsForTenantQuery,
  useSiteOverviewQuery
} from "generated/graphql";
import { Layout } from "views/Main/Layouts";
import GeneralSiteSettings from "routes/Manage/Sites/Settings/GeneralSiteSettings";
import SiteProvisioningSettings from "routes/Manage/Sites/Settings/SiteProvisioningSettings";
import SiteOverview from "./SiteOverview";
import CreateRoomModal from "../../Rooms/Create/CreateRoomModal";
import RemoveRoomsModal from "../../Rooms/Delete/DeteleRoomModal";
import { generatePath, useRouteMatch } from "react-router";
import { ProtectedRoute, ProtectedNavLink } from "TsRoleGuards";
import { SiteAnalysisRouter } from "./SiteAnalysis";
import { useTenantId } from "polyrouter/SelectedTenantProvider";

const SiteSettingsRouter: React.FC<{
  id: string;
  name: string;
}> = ({ id, name }) => {
  return (
    <Switch>
      <Route nest path="/general">
        <GeneralSiteSettings id={id} />
      </Route>
      <ProtectedRoute nest path="/provisioning" requirement="UPDATE_SITES">
        <SiteProvisioningSettings id={id} name={name} />
      </ProtectedRoute>
      <Route nest path="/">
        <Redirect nest to="general" />
      </Route>
    </Switch>
  );
};

export const SiteDetailsRouter: React.FC = () => {
  const { t } = useTranslation();
  const { siteId } = useParams<{ siteId: string }>();
  const { url } = useRouteMatch();
  const tenantId = useTenantId();
  const { Sidebar } = Layout;
  const [selectedItems, setSelectedItems] = useState<Array<string>>([]);

  const { data, fetching, executor: refresh, stale } = useSiteOverviewQuery({
    variables: {
      id: siteId
    }
  });

  const { data: sitesData } = useSiteIdsForTenantQuery({
    variables: {
      tenantId
    }
  });

  const name = data?.getSite?.name || "";
  let rooms = data?.getSite?.rooms || [];
  const siteHasAddress =
    !!data?.getSite?.address?.formattedAddress ||
    !!data?.getSite?.address?.address1;
  const siteUnassigned = nameIsUnassigned(name);
  const deviceCount = data?.countDevices || 0;

  const roomsToDelete = selectedItems.reduce((roomsList, id) => {
    const roomObj = rooms.find(room => room.id === id);
    return roomObj ? [...roomsList, { name: roomObj.name, id }] : roomsList;
  }, [] as Array<{ name: string; id: string }>);

  if (
    sitesData?.sites &&
    !sitesData?.sites.filter(site => site.id === siteId)[0]
  ) {
    return <Redirect to="/manage/sites" />;
  }

  const backUrl = url
    .replace(`/${siteId}`, "")
    .replace("/sites/details", "/sites")
    .replace("/insight-site", "");

  return (
    <div className="site-details-content">
      <Layout
        back={{
          to: generatePath(backUrl),
          persist: ["InsightsFilterState"],
          title: t(url.includes("insights") ? "INSIGHT_DETAILS" : "SITES"),
          iconDesc: t("RETURN_TO_SITE_LIST")
        }}
      >
        <>
          <Sidebar>
            <ul className="menu router">
              <li>
                <NavLink nest exact to="">
                  {t("OVERVIEW")}
                </NavLink>
              </li>
              <>
                {(!siteUnassigned || (siteUnassigned && siteHasAddress)) && (
                  <li>
                    <NavLink nest exact={false} to="/settings">
                      {t("SETTINGS")}
                    </NavLink>
                    <Route nest path="/settings">
                      <ul className="menu router sub">
                        <li>
                          <NavLink nest exact to="/general">
                            {t("GENERAL")}
                          </NavLink>
                        </li>
                        {!siteUnassigned && (
                          <li>
                            <ProtectedNavLink
                              nest
                              exact
                              to="/provisioning"
                              requirement="UPDATE_SITES"
                            >
                              {t("DEVICE_ASSOCIATION")}
                            </ProtectedNavLink>
                          </li>
                        )}
                      </ul>
                    </Route>
                  </li>
                )}
                {!siteUnassigned && (
                  <li>
                    <NavLink nest exact={false} to="/analysis">
                      {t("ANALYSIS")}
                    </NavLink>
                    <Route nest path="/analysis">
                      <ul className="menu router sub">
                        <li>
                          <NavLink nest exact to="/rooms">
                            {t("ROOMS")}
                          </NavLink>
                        </li>
                        <li>
                          <NavLink nest exact to="/devices">
                            {t("DEVICES")}
                          </NavLink>
                        </li>
                      </ul>
                    </Route>
                  </li>
                )}
              </>
            </ul>
          </Sidebar>
          <Switch>
            <Route nest path="/settings">
              <SiteSettingsRouter id={siteId} name={name} />
            </Route>
            <Route nest path="/analysis">
              <SiteAnalysisRouter id={siteId} name={name} />
            </Route>
            <Route nest path="/">
              <SiteOverview
                selectedItems={selectedItems}
                setSelectedItems={setSelectedItems}
                id={siteId}
                name={name}
                rooms={rooms}
                deviceCount={deviceCount}
                fetching={fetching || stale}
              />
            </Route>
          </Switch>
          <Route nest path="/add">
            <CreateRoomModal
              refresh={() => {
                refresh({
                  requestPolicy: "network-only"
                });
              }}
            />
          </Route>
          <Route nest path="/remove">
            <RemoveRoomsModal
              refresh={() => {
                setSelectedItems([]);
                refresh({
                  requestPolicy: "network-only"
                });
              }}
              rooms={roomsToDelete}
            />
          </Route>
        </>
      </Layout>
    </div>
  );
};

export default SiteDetailsRouter;
